import React, { ReactNode } from 'react';
import classNames from 'classnames';
import SelectableBase, {
  ExtendableSelectableBaseProps,
} from '../SelectableBase';
import Text from '../Text';
import Box from '../Box';
import {
  ResponsiveValue,
  SizeValue,
  getResponsiveValue,
  processStylingProps,
} from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import logger from '../_internal/logger';

import styles from './radio.module.scss';

export type Container = (props: {
  radioUi: ReactNode;
  disabled: boolean;
  isSelected: boolean;
}) => JSX.Element;

interface DefaultVariantProps {
  /**
   * The variation/style of the button.
   */
  variant?: 'default';

  /**
   * Size of the radio. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default 'default'
   */
  size?: ResponsiveValue<'default' | 'small'>;
  minWidth: undefined;
}

interface BoxedVariantProps {
  variant?: 'boxed';
  size?: 'default';
  /**
   * The minimum width of the radio when using the 'boxed' variant. A `true` value means use the default `min-width`, otherwise specify a value. Defaults to `false`. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default false
   */
  minWidth?: ResponsiveValue<boolean | SizeValue>;
}

type VariantProps = DefaultVariantProps | BoxedVariantProps;

interface CommonProps {
  /**
   * Radio label contents
   */
  children?: ReactNode;

  /**
   * A render prop that wraps the radio, allowing for additional clickable content around the radio UI
   */
  container?: Container;

  /**
   * The name of the radio.
   */
  name: string;
}

type Props = VariantProps & CommonProps;

// The default container does nothing (returns back the contents)
const DEFAULT_CONTAINER: Container = ({ radioUi }) => <>{radioUi}</>;

const DEFAULT_SIZE = 'default';

export type RadioProps = Omit<ExtendableSelectableBaseProps<Props>, 'like'>;

/**
 * The `Radio` allows the user to select one item from a set. In most cases, you should use a `RadioGroup` to display a set of radios.
 */
const Radio = ({
  'aria-label': ariaLabel,
  children,
  container = DEFAULT_CONTAINER,
  minWidth = false,
  size = DEFAULT_SIZE,
  variant = 'default',
  name,
  ...rootProps
}: RadioProps) => {
  const isDefaultContainer = container === DEFAULT_CONTAINER;

  if (!children && !ariaLabel && isDefaultContainer) {
    logger.warn(
      'Radio must specify a label (via `children`), a `container` or `aria-label` to satisfy accessibility needs',
    );
  }

  const screenSize = useScreenSize();

  // Only allow min width on "boxed" variant.
  const responsiveMinWidth = getResponsiveValue(screenSize, minWidth);
  const hasMinWidth = responsiveMinWidth && variant === 'boxed';
  const boxMinWidth =
    typeof responsiveMinWidth !== 'boolean' && hasMinWidth
      ? responsiveMinWidth
      : undefined;

  const responsiveSize = getResponsiveValue(screenSize, size) || DEFAULT_SIZE;

  // Radio sets styling props on the hidden input element in SelectableBase,
  // which is unintended behavior. We will likely remove this entirely in the
  // future (as opposed to replacing it with dangerous props), so starting to
  // warn about it now. Intentionally not adding style-related props to the type
  // definition because this was unintended behavior.
  // TLDR: styling props do not do anything because they are added to hidden
  // inputs.
  const selectableBaseProps = processStylingProps(rootProps, 'Radio', {
    stylingProps: 'warn',
    dangerousStylingProps: 'remove',
  });

  return (
    <SelectableBase
      like="radio"
      aria-label={ariaLabel}
      name={name}
      {...selectableBaseProps}
    >
      {({ input, isSelected, disabled, ...boxProps }) => (
        <Box
          className={classNames(styles.root, styles[`size-${responsiveSize}`], {
            [styles['is-boxed']]: variant === 'boxed',
            [styles['is-selected']]: isSelected,
            [styles['is-disabled']]: disabled,
            [styles['has-min-width']]: hasMinWidth,
          })}
          {...boxProps}
          minWidth={boxMinWidth}
        >
          {container({
            isSelected,
            disabled,
            radioUi: (
              <Box className={styles.container}>
                <Box className={styles['toggle-wrapper']}>
                  <span className={styles.toggle}>{input}</span>
                </Box>
                {children && (
                  <Text
                    scale={responsiveSize === 'default' ? '3' : '2'}
                    height="standard"
                    family="regular"
                    color={disabled ? 'gray-45' : undefined}
                    className={styles.content}
                  >
                    {children}
                  </Text>
                )}
              </Box>
            ),
          })}
        </Box>
      )}
    </SelectableBase>
  );
};

export default Radio;
